import { useState, useMemo } from 'react';
import Header from '@/pages/home/components/Header';
import Footer from '@/pages/home/components/Footer';
import {
  recomendaciones,
  bloquesInfo,
  reglasDescubrimiento,
  statsDescubrimiento,
  tiposEntidad,
  getRecomendacionesPorBloque,
  getRecomendacionesPorTipo,
  getTopPorInteraccion,
  getRecomendacionesEnVivo,
  getTodasPorBloque,
  formatearNumero,
  type BloqueDescubrimiento,
  type TipoEntidadDescubrimiento,
  type RecomendacionDescubrimiento,
} from '@/mocks/sistemaDescubrimiento';

function RecomendacionCard({ rec, onOpenDetail }: { rec: RecomendacionDescubrimiento; onOpenDetail: (r: RecomendacionDescubrimiento) => void }) {
  const tipoInfo = tiposEntidad.find(t => t.id === rec.tipo);
  const bloqueInfo = bloquesInfo.find(b => b.id === rec.bloque);

  return (
    <div
      onClick={() => onOpenDetail(rec)}
      className="group bg-background-50 rounded-2xl border border-background-200/70 overflow-hidden hover:border-background-300/60 transition-all cursor-pointer"
    >
      <div className="relative h-44 overflow-hidden">
        <img src={rec.imagenUrl} alt={rec.nombre} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" loading="lazy" />
        <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/70 via-foreground-950/10 to-transparent"></div>
        <div className="absolute top-3 left-3 flex items-center gap-1.5">
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold border ${tipoInfo?.color} ${tipoInfo?.bg} bg-white/90`}>
            <i className={`${tipoInfo?.icono} text-[9px]`}></i>
            {tipoInfo?.label}
          </span>
          {rec.estaEnVivo && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-500 text-white text-[9px] font-bold">
              <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse"></span>
              EN VIVO
            </span>
          )}
        </div>
        <div className="absolute top-3 right-3">
          {bloqueInfo && (
            <span className={`inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full text-[9px] font-bold ${bloqueInfo.bg} ${bloqueInfo.color}`}>
              <i className={`${bloqueInfo.icono} text-[8px]`}></i>
              {bloqueInfo.titulo}
            </span>
          )}
        </div>
        <div className="absolute bottom-3 left-3 right-3">
          <div className="flex items-center gap-2">
            <img src={rec.logoUrl} alt="" className="w-6 h-6 rounded-full object-cover ring-1 ring-white/30" />
            <h3 className="text-sm font-bold text-white truncate drop-shadow-md">{rec.nombre}</h3>
          </div>
        </div>
      </div>

      <div className="p-4 space-y-3">
        <p className="text-xs text-foreground-700 line-clamp-2">{rec.subtitulo}</p>

        <div className="flex items-center gap-3 text-[10px] text-foreground-500">
          <span className="flex items-center gap-1" title="Likes">
            <i className="ri-heart-line text-[9px] text-rose-500"></i>
            {rec.senales.likes}
          </span>
          <span className="flex items-center gap-1" title="Comentarios">
            <i className="ri-chat-1-line text-[9px] text-amber-500"></i>
            {rec.senales.comentarios}
          </span>
          <span className="flex items-center gap-1" title="Compartidos">
            <i className="ri-share-forward-line text-[9px] text-emerald-500"></i>
            {rec.senales.compartidos}
          </span>
          <span className="flex items-center gap-1 ml-auto" title="Interaction Score">
            <span className={`text-[9px] font-bold ${rec.interactionScore >= 90 ? 'text-rose-600' : rec.interactionScore >= 80 ? 'text-amber-600' : 'text-emerald-600'}`}>
              <i className="ri-flashlight-line text-[8px]"></i> {rec.interactionScore}
            </span>
          </span>
        </div>

        <div className="flex items-center gap-2">
          {rec.tags.slice(0, 2).map(tag => (
            <span key={tag} className="px-1.5 py-0.5 rounded-md bg-background-100 text-foreground-500 text-[9px]">{tag}</span>
          ))}
          {rec.badge && (
            <span className="px-1.5 py-0.5 rounded-md bg-rose-100 text-rose-700 text-[9px] font-semibold">{rec.badge}</span>
          )}
        </div>
      </div>
    </div>
  );
}

function ReglaCard({ regla }: { regla: typeof reglasDescubrimiento[0] }) {
  return (
    <div className={`bg-background-50 rounded-2xl border ${regla.colorBorde} p-6 space-y-4`}>
      <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${regla.gradiente} flex items-center justify-center`}>
        <i className={`${regla.icono} text-white text-xl`}></i>
      </div>
      <h4 className="text-base font-bold text-foreground-900">{regla.titulo}</h4>
      <p className="text-sm text-foreground-600 leading-relaxed">{regla.descripcion}</p>
    </div>
  );
}

function DetailModal({ rec, onClose }: { rec: RecomendacionDescubrimiento; onClose: () => void }) {
  const tipoInfo = tiposEntidad.find(t => t.id === rec.tipo);
  const bloqueInfo = bloquesInfo.find(b => b.id === rec.bloque);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-foreground-950/60 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-background-50 rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        {/* Imagen */}
        <div className="relative h-48 rounded-t-2xl overflow-hidden">
          <img src={rec.imagenUrl} alt={rec.nombre} className="w-full h-full object-cover object-top" />
          <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/70 via-transparent to-transparent"></div>
          <button onClick={onClose} className="absolute top-4 right-4 w-8 h-8 rounded-full bg-foreground-950/50 text-white flex items-center justify-center hover:bg-foreground-950/70 transition-colors cursor-pointer">
            <i className="ri-close-line"></i>
          </button>
          <div className="absolute bottom-4 left-4 flex items-center gap-3">
            <img src={rec.logoUrl} alt="" className="w-12 h-12 rounded-full object-cover ring-2 ring-white/30" />
            <div>
              <h3 className="text-lg font-bold text-white drop-shadow-md">{rec.nombre}</h3>
              <div className="flex items-center gap-2 mt-0.5">
                {tipoInfo && (
                  <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-bold border ${tipoInfo.color} ${tipoInfo.bg}`}>
                    <i className={`${tipoInfo.icono} text-[8px]`}></i>
                    {tipoInfo.label}
                  </span>
                )}
                {rec.estaEnVivo && (
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-red-500 text-white text-[9px] font-bold">
                    <span className="w-1 h-1 rounded-full bg-white animate-pulse"></span>
                    EN VIVO
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="p-5 space-y-5">
          {/* Stats Grid */}
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-background-100 rounded-xl p-3 text-center">
              <p className="text-xl font-bold text-foreground-950">{rec.interactionScore}</p>
              <p className="text-[10px] text-foreground-500">Interacción</p>
            </div>
            <div className="bg-background-100 rounded-xl p-3 text-center">
              <p className="text-xl font-bold text-foreground-950">{rec.popularityScore}</p>
              <p className="text-[10px] text-foreground-500">Popularidad</p>
            </div>
            <div className="bg-background-100 rounded-xl p-3 text-center">
              <p className="text-xl font-bold text-foreground-950">#{rec.posicionBloque}</p>
              <p className="text-[10px] text-foreground-500">Posición</p>
            </div>
          </div>

          {/* Señales de actividad */}
          <div>
            <p className="text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-3">Señales de Interacción</p>
            <div className="grid grid-cols-4 gap-3">
              <div className="text-center">
                <div className="w-9 h-9 mx-auto rounded-lg bg-rose-100 flex items-center justify-center mb-1">
                  <i className="ri-heart-line text-rose-600 text-sm"></i>
                </div>
                <p className="text-sm font-bold text-foreground-900">{rec.senales.likes}</p>
                <p className="text-[9px] text-foreground-500">Likes</p>
              </div>
              <div className="text-center">
                <div className="w-9 h-9 mx-auto rounded-lg bg-amber-100 flex items-center justify-center mb-1">
                  <i className="ri-chat-1-line text-amber-600 text-sm"></i>
                </div>
                <p className="text-sm font-bold text-foreground-900">{rec.senales.comentarios}</p>
                <p className="text-[9px] text-foreground-500">Comentarios</p>
              </div>
              <div className="text-center">
                <div className="w-9 h-9 mx-auto rounded-lg bg-emerald-100 flex items-center justify-center mb-1">
                  <i className="ri-share-forward-line text-emerald-600 text-sm"></i>
                </div>
                <p className="text-sm font-bold text-foreground-900">{rec.senales.compartidos}</p>
                <p className="text-[9px] text-foreground-500">Compartidos</p>
              </div>
              <div className="text-center">
                <div className="w-9 h-9 mx-auto rounded-lg bg-violet-100 flex items-center justify-center mb-1">
                  <i className="ri-user-add-line text-violet-600 text-sm"></i>
                </div>
                <p className="text-sm font-bold text-foreground-900">{rec.senales.seguimientos}</p>
                <p className="text-[9px] text-foreground-500">Seguidos</p>
              </div>
            </div>
          </div>

          {/* Metadata */}
          <div className="grid grid-cols-2 gap-3 text-xs">
            <div className="flex items-center gap-2 text-foreground-600">
              <i className="ri-map-pin-line text-foreground-400"></i>
              {rec.ciudad}, {rec.pais}
            </div>
            <div className="flex items-center gap-2 text-foreground-600">
              <i className="ri-price-tag-3-line text-foreground-400"></i>
              {rec.categoria}
            </div>
          </div>

          {/* Motivo */}
          {bloqueInfo && (
            <div className={`${bloqueInfo.bg} rounded-xl p-4 border ${bloqueInfo.borde}`}>
              <div className="flex items-center gap-2 mb-1">
                <i className={`${bloqueInfo.icono} ${bloqueInfo.color}`}></i>
                <span className={`text-xs font-bold ${bloqueInfo.color}`}>{bloqueInfo.titulo}</span>
              </div>
              <p className="text-xs text-foreground-700 italic">"{rec.motivo}"</p>
            </div>
          )}

          {/* Tags */}
          <div className="flex flex-wrap gap-1.5">
            {rec.tags.map(tag => (
              <span key={tag} className="px-2 py-1 rounded-md bg-background-100 text-foreground-600 text-[10px]">{tag}</span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function SistemaDescubrimientoPage() {
  const [activeTab, setActiveTab] = useState<BloqueDescubrimiento | 'todos' | 'reglas'>('todos');
  const [selectedTipo, setSelectedTipo] = useState<TipoEntidadDescubrimiento | 'todas'>('todas');
  const [detailRec, setDetailRec] = useState<RecomendacionDescubrimiento | null>(null);

  const filteredRecs = useMemo(() => {
    let items = activeTab === 'todos' || activeTab === 'reglas'
      ? recomendaciones
      : getRecomendacionesPorBloque(activeTab);

    if (selectedTipo !== 'todas') {
      items = items.filter(r => r.tipo === selectedTipo);
    }

    return items.sort((a, b) => b.interactionScore - a.interactionScore);
  }, [activeTab, selectedTipo]);

  const top10Interaccion = getTopPorInteraccion(10);
  const enVivo = getRecomendacionesEnVivo();
  const porBloque = getTodasPorBloque();

  return (
    <div className="min-h-screen bg-background-50">
      <Header />

      <main>
        {/* Hero */}
        <section className="relative w-full h-[420px] md:h-[520px] overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Intelligent%20recommendation%20algorithm%20visualization%20with%20interconnected%20nodes%20and%20data%20streams%2C%20dark%20sophisticated%20analytics%20center%20with%20warm%20rose%20and%20amber%20accent%20lights%2C%20abstract%20network%20graph%20representing%20content%20discovery%2C%20modern%20AI%20interface%20aesthetic%20with%20glowing%20connection%20lines%2C%20clean%20futuristic%20atmosphere%2C%20editorial%20photography%20with%20moody%20ambient%20lighting%20and%20harmonious%20composition&width=1600&height=700&seq=descubrimiento-hero&orientation=landscape"
            alt="Sistema de Descubrimiento"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-foreground-950/40 via-foreground-950/20 to-foreground-950/70"></div>

          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center px-4 w-full">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-rose-500/20 backdrop-blur-sm text-rose-300 text-xs font-bold border border-rose-500/30 mb-5">
                <span className="w-2 h-2 rounded-full bg-rose-400 animate-pulse"></span>
                SISTEMA DE DESCUBRIMIENTO
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">Encontrá Tu Próximo<br/>Contenido Favorito</h1>
              <p className="text-base md:text-lg text-white/70 max-w-xl mx-auto">Recomendaciones inteligentes basadas en tus interacciones reales. Priorizamos calidad de participación sobre cantidad de oyentes.</p>

              {/* Overlay stats */}
              <div className="flex flex-wrap justify-center gap-6 mt-8">
                <div className="bg-foreground-950/50 backdrop-blur-sm rounded-xl px-4 py-3 text-center">
                  <p className="text-xl font-bold text-white">{statsDescubrimiento.totalRecomendaciones}</p>
                  <p className="text-[10px] text-white/60">Recomendaciones</p>
                </div>
                <div className="bg-foreground-950/50 backdrop-blur-sm rounded-xl px-4 py-3 text-center">
                  <p className="text-xl font-bold text-rose-400">{statsDescubrimiento.bloquesActivos}</p>
                  <p className="text-[10px] text-white/60">Bloques</p>
                </div>
                <div className="bg-foreground-950/50 backdrop-blur-sm rounded-xl px-4 py-3 text-center">
                  <p className="text-xl font-bold text-emerald-400">{statsDescubrimiento.precisionAlgoritmo}</p>
                  <p className="text-[10px] text-white/60">Precisión</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Stats Bar + Tabs */}
        <div className="sticky top-0 z-40 bg-background-50/95 backdrop-blur-md border-b border-background-200/70">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            {/* Stats row */}
            <div className="flex items-center justify-between py-3">
              <a href="/" className="text-xs text-foreground-500 hover:text-foreground-700 flex items-center gap-1 cursor-pointer whitespace-nowrap">
                <i className="ri-arrow-left-line"></i> RFW
              </a>
              <div className="flex items-center gap-6 text-xs">
                <span className="text-foreground-500">
                  <span className="font-semibold text-foreground-900">{statsDescubrimiento.totalRecomendaciones}</span> recomendaciones
                </span>
                <span className="text-foreground-500 hidden sm:inline">
                  <span className="font-semibold text-rose-600">{statsDescubrimiento.bloquesActivos}</span> bloques
                </span>
                <span className="text-foreground-500 hidden sm:inline">
                  <span className="font-semibold text-emerald-600">{statsDescubrimiento.precisionAlgoritmo}</span> precisión
                </span>
              </div>
            </div>

            {/* Tabs row */}
            <div className="flex flex-wrap items-center gap-2 pb-3">
              {[
                { id: 'todos' as const, label: 'Todas', icono: 'ri-apps-line' },
                ...bloquesInfo.map(b => ({ id: b.id as BloqueDescubrimiento, label: b.titulo, icono: b.icono })),
                { id: 'reglas' as const, label: 'Reglas', icono: 'ri-scales-3-line' },
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => { setActiveTab(tab.id); setSelectedTipo('todas'); }}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-all cursor-pointer ${
                    activeTab === tab.id
                      ? 'bg-rose-500 text-white'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200/70'
                  }`}
                >
                  <i className={`${tab.icono} text-[10px]`}></i>
                  {tab.label}
                  {tab.id !== 'reglas' && tab.id !== 'todos' && (
                    <span className="text-[9px] opacity-70">({getRecomendacionesPorBloque(tab.id).length})</span>
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
          {/* Descripción del bloque activo */}
          {activeTab !== 'todos' && activeTab !== 'reglas' && (() => {
            const b = bloquesInfo.find(bl => bl.id === activeTab);
            return b ? (
              <div className={`${b.bg} rounded-xl p-4 border ${b.borde} mb-8`}>
                <div className="flex items-center gap-2 mb-1">
                  <i className={`${b.icono} ${b.color} text-lg`}></i>
                  <span className={`text-sm font-bold ${b.color}`}>{b.titulo}</span>
                </div>
                <p className="text-xs text-foreground-600">{b.descripcion}</p>
              </div>
            ) : null;
          })()}

          {/* Filtro por tipo de entidad */}
          {activeTab !== 'reglas' && (
            <div className="flex flex-wrap items-center gap-2 mb-8">
              <span className="text-xs text-foreground-500 mr-1">Filtrar:</span>
              {[
                { id: 'todas' as const, label: 'Todas', icono: 'ri-apps-line', color: 'text-foreground-600', bg: 'bg-background-100' },
                ...tiposEntidad.map(t => ({ id: t.id, label: t.label, icono: t.icono, color: t.color, bg: t.bg })),
              ].map(filtro => (
                <button
                  key={filtro.id}
                  onClick={() => setSelectedTipo(filtro.id)}
                  className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold whitespace-nowrap transition-all cursor-pointer border ${
                    selectedTipo === filtro.id
                      ? `${filtro.bg} ${filtro.color} border-current`
                      : 'bg-background-50 text-foreground-500 border-background-200/70 hover:border-background-300/60'
                  }`}
                >
                  <i className={`${filtro.icono} text-[9px]`}></i>
                  {filtro.label}
                </button>
              ))}
            </div>
          )}

          {/* Grid de recomendaciones */}
          {activeTab !== 'reglas' && (
            <>
              {filteredRecs.length === 0 ? (
                <div className="py-20 text-center">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-background-100 flex items-center justify-center">
                    <i className="ri-search-line text-2xl text-foreground-400"></i>
                  </div>
                  <h3 className="text-lg font-bold text-foreground-900 mb-1">Sin resultados</h3>
                  <p className="text-sm text-foreground-500 max-w-sm mx-auto">Probá cambiando los filtros para descubrir más contenido.</p>
                  <button
                    onClick={() => { setActiveTab('todos'); setSelectedTipo('todas'); }}
                    className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-rose-100 text-rose-700 text-xs font-semibold hover:bg-rose-200 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-refresh-line"></i>
                    Limpiar filtros
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                  {filteredRecs.map(rec => (
                    <RecomendacionCard key={rec.id} rec={rec} onOpenDetail={setDetailRec} />
                  ))}
                </div>
              )}
            </>
          )}

          {/* Vista Reglas */}
          {activeTab === 'reglas' && (
            <div className="space-y-10">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {reglasDescubrimiento.map(r => (
                  <ReglaCard key={r.id} regla={r} />
                ))}
              </div>

              {/* Stats Summary */}
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
                <div className="bg-background-100 rounded-xl p-4 text-center">
                  <p className="text-2xl font-bold text-foreground-950">{statsDescubrimiento.totalRecomendaciones}</p>
                  <p className="text-[10px] text-foreground-500">Recomendaciones Totales</p>
                </div>
                <div className="bg-background-100 rounded-xl p-4 text-center">
                  <p className="text-2xl font-bold text-rose-600">{statsDescubrimiento.bloquesActivos}</p>
                  <p className="text-[10px] text-foreground-500">Bloques Activos</p>
                </div>
                <div className="bg-background-100 rounded-xl p-4 text-center">
                  <p className="text-2xl font-bold text-emerald-600">{statsDescubrimiento.precisionAlgoritmo}</p>
                  <p className="text-[10px] text-foreground-500">Precisión Algoritmo</p>
                </div>
                <div className="bg-background-100 rounded-xl p-4 text-center">
                  <p className="text-2xl font-bold text-amber-600">{statsDescubrimiento.interaccionPromedio}</p>
                  <p className="text-[10px] text-foreground-500">Score Promedio</p>
                </div>
                <div className="bg-background-100 rounded-xl p-4 text-center">
                  <p className="text-2xl font-bold text-foreground-950">{statsDescubrimiento.entidadesCubiertas}</p>
                  <p className="text-[10px] text-foreground-500">Entidades Cubiertas</p>
                </div>
              </div>

              {/* Regla final */}
              <div className="text-center py-6">
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-rose-100 text-rose-700 text-xs font-bold">
                  <i className="ri-admin-line"></i>
                  REGLA DE ORO
                </div>
                <p className="mt-3 text-sm text-foreground-600 italic max-w-lg mx-auto">"Priorizar interacción sobre popularidad. Una comunidad que comenta, comparte y participa activamente vale más que mil oyentes pasivos. El algoritmo de descubrimiento de RFW pondera las señales de engagement por encima de las métricas de audiencia bruta."</p>
              </div>
            </div>
          )}

          {/* CTA dual */}
          {activeTab !== 'reglas' && (
            <div className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-4">
              <a href="/explorar" className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-rose-500 text-white font-bold text-sm hover:bg-rose-600 transition-all cursor-pointer whitespace-nowrap">
                <i className="ri-compass-3-line"></i>
                Explorar Más Contenido
              </a>
              <a href="/tv-app" className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-background-100 text-foreground-700 font-bold text-sm hover:bg-background-200/70 transition-all cursor-pointer whitespace-nowrap border border-background-200/70">
                <i className="ri-tv-line"></i>
                Ver en TV App
              </a>
            </div>
          )}
        </div>
      </main>

      <Footer />
      <div className="md:hidden h-20"></div>

      {/* Detail Modal */}
      {detailRec && (
        <DetailModal rec={detailRec} onClose={() => setDetailRec(null)} />
      )}
    </div>
  );
}